import { fmt, type CalcResult, type Scenario } from "@h2lca/core";
import { useEffect, useRef } from "react";
import { useMaster } from "@/store/master";
import { STEPS, STEP_IDS, type StepId } from "@/store/workshop";

/**
 * 산정 화면 왼쪽의 세로 단계 목록. 단계마다 상태 한 줄을 붙인다 , 목록만 보고도
 * "어디까지 왔고, 어디가 비었고, 결과가 얼마인지" 가 읽혀야 한다.
 */
export function StepNav({ scenario, result, current, onChange }: { scenario: Scenario; result: CalcResult; current: StepId; onChange: (s: StepId) => void }) {
  const data = useMaster((s) => s.data);
  const tech = data.technologies.find((t) => t.id === scenario.basis.productionTechId);
  const errors = result.issues.filter((i) => i.level === "error");
  const warnings = result.issues.filter((i) => i.level === "warning");
  const inCount = result.byEquipment.filter((e) => e.inBoundary).length;
  const outCount = result.byEquipment.length - inCount;
  const inputErrors = errors.filter((i) => i.step === "inputs" || i.step === "electricity").length;
  const curIdx = STEP_IDS.indexOf(current);
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const revealCurrent = () => {
      const item = nav.querySelector<HTMLElement>('[aria-current="step"]');
      if (!item || nav.scrollWidth <= nav.clientWidth) return;
      const container = nav.getBoundingClientRect();
      const active = item.getBoundingClientRect();
      if (active.left < container.left) nav.scrollLeft -= container.left - active.left;
      else if (active.right > container.right) nav.scrollLeft += active.right - container.right;
    };
    revealCurrent();
    const resize = new ResizeObserver(revealCurrent);
    resize.observe(nav);
    return () => resize.disconnect();
  }, [current]);

  const status: Record<StepId, { text: string; error?: boolean }> = {
    basis: { text: `${tech?.name ?? scenario.basis.productionTechId}, 기준 ${fmt(scenario.basis.batchH2Kg)} kg`, error: errors.some((i) => i.step === "basis") },
    fu: { text: `수소 ${scenario.functionalUnit.h2Kg} kg, 순도 ${scenario.functionalUnit.purityPct}%` },
    equipment: { text: scenario.equipment.length ? `설비 ${scenario.equipment.length}개` : "설비 없음", error: scenario.equipment.length === 0 },
    boundary: { text: scenario.equipment.length ? `포함 ${inCount}, 제외 ${outCount}` : "없음", error: errors.some((i) => i.step === "boundary") },
    inputs: { text: inputErrors ? `필수값 누락과 오류 ${inputErrors}` : warnings.length ? `경고 ${warnings.length}` : "입력 완료", error: inputErrors > 0 },
    calc: { text: errors.length ? `오류 ${errors.length}건` : "계산식 확인", error: errors.length > 0 },
    result: { text: result.perKgH2 === null ? "계산 제한" : `${fmt(result.perKgH2, 2)} kgCO2eq/kgH2`, error: result.perKgH2 === null },
  };

  return (
    <nav ref={navRef} className="stepnav" aria-label="산정 단계">
      {STEPS.map((s, i) => {
        const st = status[s.id];
        const done = i < curIdx && !st.error;
        return (
          <button key={s.id} type="button" className="stepnav-item" aria-current={current === s.id ? "step" : undefined} data-done={done ? "true" : undefined} data-error={st.error ? "true" : undefined} onClick={() => onChange(s.id)}>
            <span className="stepnav-num" aria-hidden>
              {done ? "✓" : i + 1}
            </span>
            <span className="min-w-0 flex-1">
              <span className="stepnav-label">{s.label}</span>
              <span className="stepnav-status" title={st.text}>
                {st.text}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}
